import { Button } from "@-/dev.improve.haniverse.components.ui.button";
import { Input, PasswordInput } from "@-/dev.improve.haniverse.components.ui.input";
import { Label } from "@-/dev.improve.haniverse.components.ui.label";
import {
  Surface,
  SurfaceContent,
  SurfaceDescription,
  SurfaceHeader,
  SurfaceTitle,
} from "@-/dev.improve.haniverse.components.ui.surface";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { type TextInput, View } from "@-/com.npmjs.react-native";
import * as React from "react";

export type ResetPasswordFormProps = {
  /** Disables the inputs and primary button while an auth request is in flight. */
  busy?: boolean;
  /** Controlled verification-code / reset-token value; falls back to internal state when omitted. */
  code?: string;
  onChangeCode?: (value: string) => void;
  onChangePassword?: (value: string) => void;
  /** Submit handler. Receives the current field values. */
  onSubmit?: (values: { code: string; password: string }) => void;
  /** Controlled new-password value; falls back to internal uncontrolled state when omitted. */
  password?: string;
};

export function ResetPasswordForm({
  busy = false,
  code: codeProp,
  onChangeCode,
  onChangePassword,
  onSubmit,
  password: passwordProp,
}: ResetPasswordFormProps = {}) {
  const codeInputRef = React.useRef<TextInput>(null);

  const [passwordState, setPasswordState] = React.useState("");
  const [codeState, setCodeState] = React.useState("");
  const password = passwordProp ?? passwordState;
  const code = codeProp ?? codeState;

  function handleChangePassword(value: string) {
    if (onChangePassword != null) {
      onChangePassword(value);
    } else {
      setPasswordState(value);
    }
  }

  function handleChangeCode(value: string) {
    if (onChangeCode != null) {
      onChangeCode(value);
    } else {
      setCodeState(value);
    }
  }

  function onPasswordSubmitEditing() {
    codeInputRef.current?.focus();
  }

  function handleSubmit() {
    onSubmit?.({ code, password });
  }

  return (
    <View className="gap-6">
      <Surface className="border-border/0 shadow-none sm:border-border sm:shadow-sm sm:shadow-black/5">
        <SurfaceHeader>
          <SurfaceTitle className="text-center text-xl sm:text-left">Reset password</SurfaceTitle>
          <SurfaceDescription className="text-center sm:text-left">
            Enter the code sent to your email and set a new password
          </SurfaceDescription>
        </SurfaceHeader>
        <SurfaceContent className="gap-6">
          <View className="gap-6">
            <View className="gap-1.5">
              <View className="flex-row items-center">
                <Label htmlFor="password">New password</Label>
              </View>
              <PasswordInput
                editable={!busy}
                id="password"
                onChangeText={handleChangePassword}
                onSubmitEditing={onPasswordSubmitEditing}
                returnKeyType="next"
                submitBehavior="submit"
                value={password}
              />
            </View>
            <View className="gap-1.5">
              <Label htmlFor="code">Verification code</Label>
              <Input
                autoCapitalize="none"
                autoComplete="sms-otp"
                editable={!busy}
                id="code"
                keyboardType="numeric"
                onChangeText={handleChangeCode}
                onSubmitEditing={handleSubmit}
                ref={codeInputRef}
                returnKeyType="send"
                textContentType="oneTimeCode"
                value={code}
              />
            </View>
            <Button className="w-full" disabled={busy} onPress={handleSubmit}>
              <Text>Reset Password</Text>
            </Button>
          </View>
        </SurfaceContent>
      </Surface>
    </View>
  );
}
